/* eslint-disable react-hooks/exhaustive-deps */
import {
  Alert,
  Button,
  Box,
  Divider,
  Flex,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Space,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import CSpinner from '../../../components/CSpinner';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  AuthenticationInfoRes,
  EnabledPolicyTypeEnum,
  IdSyncStatus,
  ProvisionModalState,
  ProvisionStatusEnum,
  RejectProvisionReason,
  TokenStatus,
} from '../../../types/provision';
import { ProvisionApi } from '@Apis';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import {
  AppInfoForm,
  DataSyncFailed,
  DataSynced,
  DataSyncing,
  EnablePolicyRadioGroup,
  ProvisionOrgInfo,
  StepIcon,
  UserIcon,
} from './share';
import { reportPendo } from '@Utils/setupPendo';
import { deleteCookie, getProvisionCookie } from '@Utils/common';
import { CAS_DOC_LINK, getCasOLHLink } from '@Utils/casDocLink';
import { Steps } from 'antd';
import i18next from 'i18next';
import RejectProvisionModal from './rejectProvisionModal';
import { Service } from '@Types/app';

enum TeamsChatStatus {
  NotProvision = 0,
  Provisioning = 1,
  Provisioned = 2,
}

const SERVICE = Service.TeamsChat;

interface TeamsChatModalState extends ProvisionModalState {
  isProtectOwnApp?: boolean; //use to teams chat: Provision with Your Own App
}

const TeamsChatModal = ({
  orgId,
  onClose,
  isOpen,
  provisionStatus,
  isProtectOwnApp = false,
}: TeamsChatModalState) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation(['provision', 'common']);
  const StatusMap = {
    [ProvisionStatusEnum.NotStarted]: TeamsChatStatus.NotProvision,
    [ProvisionStatusEnum.Provisioning]: TeamsChatStatus.Provisioning,
    [ProvisionStatusEnum.Provisioned]: TeamsChatStatus.Provisioned,
  };

  const [syncDataFailFlag, setSyncDataFailFlag] = useState(
    provisionStatus.status === ProvisionStatusEnum.Failure &&
      [IdSyncStatus.Failure, 'FAILURE'].includes(
        provisionStatus?.idSync?.status
      )
  );
  const [teamsChatStatus, setTeamsChatStatus] = useState<TeamsChatStatus>(
    // StatusMap[provisionStatus.status] || TeamsChatStatus.NotProvision
    () => {
      if (Object.keys(StatusMap).includes(provisionStatus.status)) {
        return StatusMap[provisionStatus.status as keyof typeof StatusMap];
      } else {
        //provisionStatus.status = failure
        //token failure
        if (provisionStatus.authorization.status === TokenStatus.Failure) {
          return TeamsChatStatus.NotProvision;
        } else {
          //id sync fail
          return TeamsChatStatus.Provisioning;
          //TODO: 同时设置 id sync 为失败的情况
        }
      }
    }
  );
  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | AuthenticationInfoRes>(null);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const [rejectReason, setRejectReason] =
    useState<RejectProvisionReason | null>(null);
  useEffect(() => {
    if (teamsChatStatus !== TeamsChatStatus.NotProvision) {
      return;
    }

    if (isProtectOwnApp) {
      ProvisionApi.apiGetTeamsChatRenewAuthentications(orgId)
        .then((res) => {
          setAuthenticationInfo(res.data);
        })
        .catch((e) => {
          console.error(JSON.stringify(e));
        });
    } else {
      ProvisionApi.apiGetAuthenticationInfo(SERVICE, orgId)
        .then((res) => {
          setAuthenticationInfo(res.data);
        })
        .catch((e) => {
          console.error(JSON.stringify(e));
          const errorCode = e.data?.error?.details?.[0].errorCode;
          if (
            errorCode === RejectProvisionReason.ServiceConflict ||
            errorCode === RejectProvisionReason.PocConflict
          ) {
            setRejectReason(errorCode);
          }
        });
    }
  }, []);

  if (rejectReason) {
    return (
      <RejectProvisionModal
        onClose={onClose}
        service={SERVICE}
        reason={rejectReason}
      />
    );
  }

  if (teamsChatStatus === TeamsChatStatus.NotProvision && !authenticationInfo) {
    return <></>;
  }

  const titleTextStyle = {
    fontSize: '16px',
    lineHeight: '22px',
    fontWeight: '400',
    color: colorStyle.color.primary,
  };

  return (
    <>
      <Modal isOpen={isOpen} isClosable onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          {/* TODO: i18n: "Grant Access to Exchange Online" */}
          <ModalHeader>
            {isProtectOwnApp
              ? t('provision.service.teamsChat.title')
              : t('administration.sa.teamsChatProtectOwnApp')}
          </ModalHeader>
          <ModalBody>
            <ProvisionOrgInfo orgId={orgId} />
            <Alert border="0px" severity="info" variant="outline" px="0" pt="0">
              <Text
                fontSize="14px"
                lineHeight="20px"
                fontWeight="400"
                color={colorStyle.color.secondary}
              >
                <Trans
                  i18nKey="provision.service.teamsChat.note"
                  ns="provision"
                >
                  placeholder
                  <Link
                    href={getCasOLHLink(
                      CAS_DOC_LINK.provision.teamsChat.note,
                      i18next.language
                    )}
                    target="_blank"
                  >
                    placeholder
                  </Link>
                  placeholder
                </Trans>
              </Text>
            </Alert>
            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <Flex direction="column" gap="16px">
              <Steps
                direction="vertical"
                current={teamsChatStatus + 1}
                items={[
                  {
                    icon: <UserIcon />,
                    title: (
                      <Text {...titleTextStyle}>
                        <Trans
                          i18nKey="provision.service.teamsChat.installApp"
                          ns="provision"
                        >
                          placeholder
                          <Link
                            href={getCasOLHLink(
                              CAS_DOC_LINK.provision.teamsChat.instruction,
                              i18next.language
                            )}
                            target="_blank"
                          >
                            placeholder
                          </Link>
                          placeholder
                        </Trans>
                      </Text>
                    ),
                    description: <Space height="16px" />,
                  },
                  {
                    icon: (
                      <StepIcon
                        checked={
                          teamsChatStatus >= TeamsChatStatus.Provisioning
                        }
                      />
                    ),
                    title: (
                      <Text pt="4px" {...titleTextStyle}>
                        {t('provision.service.teamsChat.provideAppInfo')}
                      </Text>
                    ),
                    description:
                      teamsChatStatus === TeamsChatStatus.NotProvision ? (
                        <GrantPermissionAndEnablePolicy
                          authenticationInfo={authenticationInfo}
                          setTeamsChatStatus={setTeamsChatStatus}
                          setErrorMsg={setErrorMsg}
                          orgId={orgId}
                          isProtectOwnApp={isProtectOwnApp}
                        />
                      ) : (
                        <Text
                          color={colorStyle.color.tertiary}
                          pt="8px"
                          lineHeight="20px"
                        >
                          {t('provision.common.permissionGranted')}
                        </Text>
                      ),
                  },
                ]}
              />
              {teamsChatStatus === TeamsChatStatus.Provisioning &&
                (syncDataFailFlag ? (
                  <DataSyncFailed
                    service={SERVICE}
                    onRetry={async () => {
                      await ProvisionApi.apiRetrySyncData(SERVICE, orgId);
                      setTeamsChatStatus(TeamsChatStatus.Provisioning);
                      setSyncDataFailFlag(false);
                    }}
                  />
                ) : (
                  <DataSyncing
                    onRefresh={async () => {
                      const res = await ProvisionApi.apiGetProvisionStatus(
                        SERVICE,
                        orgId
                      );
                      if (res.data.status === ProvisionStatusEnum.Provisioned) {
                        setTeamsChatStatus(TeamsChatStatus.Provisioned);
                      }
                    }}
                  />
                ))}
              {teamsChatStatus === TeamsChatStatus.Provisioned && (
                <DataSynced />
              )}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default TeamsChatModal;

const GrantPermissionAndEnablePolicy = ({
  authenticationInfo,
  setTeamsChatStatus,
  setErrorMsg,
  orgId,
  isProtectOwnApp,
}: {
  authenticationInfo: AuthenticationInfoRes | null;
  setTeamsChatStatus: (status: TeamsChatStatus) => void;
  setErrorMsg: (msg: string | undefined) => void;
  orgId: string;
  isProtectOwnApp: boolean;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const radioGroupRef = useRef<{
    enabledPolicyType: EnabledPolicyTypeEnum;
  }>({
    enabledPolicyType: EnabledPolicyTypeEnum.Default,
  });
  const formRef = useRef<any>(null);
  const { t } = useTranslation(['provision', 'common']);
  const [loading, toggleLoading] = useToggle(false);

  const timeRef = useRef<any>(null);
  const onClick = async () => {
    const authUrl = authenticationInfo?.[0]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    let formData: any = {};
    try {
      formData = formRef.current?.getData();
      console.log(formData);
    } catch (error) {
      console.error(error);
      return;
    }
    reportPendo('Provision.GrantPermission.Click', {
      orgId,
      service: 'teamsChat',
    });
    setErrorMsg(undefined);
    toggleLoading(true);
    window.open(authUrl + `&client_id=${formData.appId}`, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const teamsChatProvisionInfo = getProvisionCookie(
        'provision_status_azuread_graph_teams_chat'
      );
      if (teamsChatProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(teamsChatProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            let res: any;
            if (!isProtectOwnApp) {
              res = await ProvisionApi.apiSaveProvisionInfoForTeamsChat(
                code,
                formData.paymentModel,
                formData.appId,
                formData.appSecret,
                radioGroupRef.current.enabledPolicyType,
                orgId
              );
            } else {
              res = await ProvisionApi.apiRenewTeamsChat(
                code,
                formData.paymentModel,
                formData.appId,
                formData.appSecret,
                orgId
              );
            }
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.teamsChat.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setTeamsChatStatus(TeamsChatStatus.Provisioning);
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t('provision.service.teamsChat.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };

  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  return (
    <>
      {loading && <CSpinner />}
      <Flex direction="column" pt="4px" gap="16px">
        <AppInfoForm ref={formRef} />
        <Divider variant="dashed" orientation="horizontal" />
        <Text color={colorStyle.color.secondary}>
          {/* TODO: i18n "Select the policy to enable when the access grant is completed:" */}
          {t('provision.common.selectPolicyToEnable')}
        </Text>
        <EnablePolicyRadioGroup
          service={Service.TeamsChat}
          ref={radioGroupRef}
        />
        <Divider variant="dashed" orientation="horizontal" />
        <Box>
          <Button variant="secondary" borderRadius="2rem" onClick={onClick}>
            {t('provision.button.grantPermission')}
          </Button>
        </Box>
      </Flex>
    </>
  );
};
